import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  memo,
} from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
} from "react-native";
import { observer } from "mobx-react";
import { Ionicons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import Colors from "../../../Resources/Color";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { toRgba } from "../../utils/colorUtils";
import Workout from "../../Firestore/Workout";
import CustomAlert from "../../Components/CustomAlert";
import HoverableTouchableOpacity from "../../Components/HoverableTouchableOpacity";
import Tooltip from "../../Components/Tooltip";
import ProtocolInfoSection from "../ProtocolInfoSection";
import WeekHeader from "./WeekHeader";
import LazyWorkoutCard from "./LazyWorkoutCard";
import {
  BlockPlanificationConstants,
  WorkoutLimits,
} from "../../../Resources/Constants";
import { useToast } from "../../contexts/ToastContext";
import {
  getScrollPosition,
  setScrollPosition,
} from "../../utils/scrollPositionCache";
import DuplicateWeekModal from "./DuplicateWeekModal";
import BlockViewTutorialBanner from "./BlockViewTutorialBanner";
import {
  ExerciseDragProvider,
  useExerciseDrag,
} from "../../contexts/ExerciseDragContext";
import { ExerciseDndProvider } from "../../Workout/shared/exerciseDrag/ExerciseDrag";
import VirtualizedWeekColumns from "./VirtualizedWeekColumns";
import { useDevice } from "../../_layout";
import GlobalExerciseHistoryFloating from "../../Exercise/History/GlobalExerciseHistoryFloating";
import exerciseHistoryStore from "../../Exercise/History/ExerciseHistoryStore";
import TutorialLinkSection from "../../Components/TutorialLinkSection";

const WeekColumnContainer = memo(
  ({
    week,
    hasWorkouts,
    columnWidth,
    getDOMElement,
    onHeightChange,
    children,
  }) => {
    const columnRef = useRef(null);

    useEffect(() => {
      if (!hasWorkouts) return undefined;
      const element = getDOMElement(columnRef.current);
      if (!element) return undefined;

      const measureHeight = () => {
        try {
          const rect = element.getBoundingClientRect();
          if (rect.height > 0) {
            onHeightChange(week, rect.height);
          }
        } catch (e) {
          // Ignore measurement errors
        }
      };

      measureHeight();

      if (typeof ResizeObserver === "undefined") {
        return undefined;
      }

      const resizeObserver = new ResizeObserver(() => {
        measureHeight();
      });
      resizeObserver.observe(element);

      return () => {
        resizeObserver.disconnect();
      };
    }, [week, hasWorkouts, getDOMElement, onHeightChange]);

    return (
      <View ref={columnRef} style={[styles.weekColumn, { width: columnWidth }]}>
        {children}
      </View>
    );
  }
);

// Inner component that uses the drag context
const BlockViewInner = observer(
  ({
    block,
    showTutorialLink = false,
    showTutorialBanner = false,
    showExerciseHistory = true,
    onSendFeedback,
    onCloseTutorialLink,
    onCloseTutorialBanner,
    onAccessOld,
    tutorialUrlPath,
  }) => {
    const { t, i18n } = useTranslation();
    const { isMobile, isIOS } = useDevice();
    const { showToast } = useToast();
    const [cachedScrollLeft, setCachedScrollLeft] = useState(null);
    const [isCacheLoaded, setIsCacheLoaded] = useState(false);
    const [showingDeleteWeekAlert, setShowingDeleteWeekAlert] = useState(false);
    const [weekToDelete, setWeekToDelete] = useState(null);
    const [editingWeek, setEditingWeek] = useState(null);
    const [showingDuplicateWeek, setShowingDuplicateWeek] = useState(false);
    const [weekToDuplicate, setWeekToDuplicate] = useState(null);
    const [hoveredAddWorkoutWeek, setHoveredAddWorkoutWeek] = useState(null);
    const [isHoveringScrollArea, setIsHoveringScrollArea] = useState(false);
    const [hoveredScrollButton, setHoveredScrollButton] = useState(null);
    const [isHoveringWeekHeader, setIsHoveringWeekHeader] = useState(false);
    const [edgeHoverLock, setEdgeHoverLock] = useState({
      left: false,
      right: false,
    });
    const [scrollIndicators, setScrollIndicators] = useState({
      canScrollLeft: false,
      canScrollRight: false,
    });
    const [maxCachedWeekHeight, setMaxCachedWeekHeight] = useState(0);
    const [initialFullRenderWeek, setInitialFullRenderWeek] = useState(null);

    const scrollMetricsRef = useRef({ scrollLeft: 0, containerWidth: 0 });
    const scrollIndicatorsRef = useRef({
      canScrollLeft: false,
      canScrollRight: false,
    });
    const edgeHoverLockRef = useRef({
      left: false,
      right: false,
    });
    const didInitScrollRef = useRef(false);
    const didInitFullRenderRef = useRef(false);
    const latestScrollLeftRef = useRef(0);
    const weekHeightCacheRef = useRef(new Map());

    // Refs for scroll synchronization
    const horizontalScrollRef = useRef(null);
    const stickyHeaderRef = useRef(null);
    const headerScrollContainerRef = useRef(null);
    const headerClipRef = useRef(null);

    // Refs for mobile touch scroll handling on header
    const touchStartRef = useRef(null);
    const touchScrollStartRef = useRef(0);

    // Get exercise drag context for auto-scroll
    const { setHorizontalScrollRef, setVerticalScrollRef } = useExerciseDrag();

    /**
     * Get the actual DOM element from a React Native Web ref
     */
    const getDOMElement = useCallback((ref) => {
      if (!ref) return null;
      // Direct DOM element
      if (
        ref.scrollLeft !== undefined &&
        typeof ref.getBoundingClientRect === "function"
      ) {
        return ref;
      }
      // React Native Web ScrollView internal
      if (ref._component) return ref._component;
      if (typeof ref.getNode === "function") return ref.getNode();
      return ref;
    }, []);

    /**
     * Get scrollable node from a React Native Web ScrollView ref
     */
    const getScrollableNodeFromRef = useCallback((ref) => {
      if (!ref) return null;

      // React Native Web ScrollView exposes getScrollableNode() which returns the actual DOM element
      const scrollableNode = ref.getScrollableNode?.();
      if (scrollableNode) {
        return scrollableNode;
      }

      // If ref already points to a DOM element, it should have scrollTop
      if (ref.scrollTop !== undefined) {
        return ref;
      }

      // Fallback for cases where the ref points to a wrapper element
      const firstChild = ref.children?.[0];
      if (firstChild && firstChild.scrollTop !== undefined) {
        return firstChild;
      }

      return null;
    }, []);

    /**
     * Find the vertical scroll container by traversing up from a reference element
     * Returns both the DOM element and the React Native ScrollView ref if available
     */
    const findScrollContainer = useCallback(() => {
      if (
        Platform.OS !== "web" ||
        typeof window === "undefined" ||
        typeof document === "undefined"
      ) {
        return { domElement: null, scrollViewRef: null };
      }

      // Helper to traverse up and find scrollable ancestor
      const findScrollableAncestor = (element) => {
        let current = element;
        while (
          current &&
          current !== document.body &&
          current !== document.documentElement
        ) {
          const style = window.getComputedStyle(current);
          const overflowY = style.overflowY;
          if (
            (overflowY === "auto" || overflowY === "scroll") &&
            current.scrollHeight > current.clientHeight
          ) {
            return current;
          }
          current = current.parentElement;
        }
        return null;
      };

      // Try to find React Native ScrollView ref by traversing up
      // Look for elements that might be React Native Web ScrollView components
      const findScrollViewRef = (element) => {
        let current = element;
        while (current && current !== document.body) {
          // Check if this element or its parent has React Native ScrollView properties
          // React Native Web ScrollView components have getScrollableNode method
          if (typeof current.getScrollableNode === "function") {
            return current;
          }
          // Check for _component with scrollTo
          if (
            current._component &&
            typeof current._component.getScrollableNode === "function"
          ) {
            return current._component;
          }
          // Check parent element
          const parent = current.parentElement;
          if (parent && typeof parent.getScrollableNode === "function") {
            return parent;
          }
          current = current.parentElement;
        }
        return null;
      };

      // Try to find from the sticky header element (our component's reference point)
      const headerElement = getDOMElement(stickyHeaderRef.current);
      let domElement = null;
      let scrollViewRef = null;

      if (headerElement) {
        const scrollableAncestor = findScrollableAncestor(headerElement);
        if (scrollableAncestor) {
          domElement = scrollableAncestor;
          scrollViewRef = findScrollViewRef(scrollableAncestor);

          // If we found a ScrollView ref, try to get the scrollable node from it
          if (scrollViewRef) {
            const scrollableNode = getScrollableNodeFromRef(scrollViewRef);
            if (scrollableNode) {
              domElement = scrollableNode;
            }
          }
        }
      }

      // Fallback: Look for common React Native Web scroll container patterns
      if (!domElement) {
        const candidates = [
          // Check for data attribute first (most reliable)
          document.querySelector("[data-vertical-scroll-container]"),
          // Check for scrollable divs inside the main app container
          ...Array.from(document.querySelectorAll("#root > div > div")).filter(
            (el) => {
              const style = window.getComputedStyle(el);
              return (
                (style.overflowY === "auto" || style.overflowY === "scroll") &&
                el.scrollHeight > el.clientHeight
              );
            }
          ),
          // Last resort: document element
          document.scrollingElement,
          document.documentElement,
        ];

        for (const el of candidates) {
          if (el && el.scrollHeight > el.clientHeight) {
            domElement = el;
            scrollViewRef = findScrollViewRef(el);

            // If we found a ScrollView ref, try to get the scrollable node from it
            if (scrollViewRef) {
              const scrollableNode = getScrollableNodeFromRef(scrollViewRef);
              if (scrollableNode) {
                domElement = scrollableNode;
              }
            }
            break;
          }
        }
      }

      return { domElement, scrollViewRef };
    }, [getDOMElement, getScrollableNodeFromRef]);

    // Register vertical scroll container for auto-scroll
    // This ensures auto-scroll works globally, not just when hovering over workout cards
    useEffect(() => {
      if (Platform.OS !== "web") return;

      const registerVerticalScroll = () => {
        const { domElement, scrollViewRef } = findScrollContainer();
        if (domElement || scrollViewRef) {
          // Register the scroll container (prefer ScrollView ref if available)
          const toRegister = scrollViewRef || domElement;
          setVerticalScrollRef(toRegister);
        }
      };

      // Register immediately
      registerVerticalScroll();

      // Also register periodically to catch dynamic changes (e.g., when content loads)
      const interval = setInterval(registerVerticalScroll, 1000);

      return () => {
        clearInterval(interval);
      };
    }, [findScrollContainer, setVerticalScrollRef]);

    /**
     * Synchronize header scroll with content scroll - SYNCHRONOUS update
     * Updates header transform immediately during scroll for zero perceived lag
     * No RAF delay - the header moves in the same frame as the scroll
     */
    const syncHeaderScroll = useCallback(
      (scrollLeft) => {
        if (Platform.OS !== "web") return;

        // Update header transform SYNCHRONOUSLY - no RAF delay
        // This makes the header feel perfectly in sync with scroll
        const headerContainer = getDOMElement(headerScrollContainerRef.current);
        if (headerContainer && headerContainer.style) {
          headerContainer.style.transform = `translate3d(-${scrollLeft}px, 0, 0)`;
        }
      },
      [getDOMElement]
    );

    /**
     * Handle wheel events on the sticky header to enable horizontal scrolling
     */
    const handleHeaderWheel = useCallback(
      (event) => {
        if (Platform.OS !== "web") return;

        // Try to get the virtual list's outer element for scrolling
        const scrollView = getDOMElement(horizontalScrollRef.current);
        if (!scrollView) return;

        // Check if this is a horizontal scroll (shift+wheel or horizontal trackpad)
        const deltaX = event.deltaX || (event.shiftKey ? event.deltaY : 0);

        if (deltaX !== 0) {
          event.preventDefault();
          scrollView.scrollLeft += deltaX;
          syncHeaderScroll(scrollView.scrollLeft);
        }
      },
      [getDOMElement, syncHeaderScroll]
    );

    /**
     * Handle touch events on the sticky header for mobile horizontal scrolling
     * Mirrors handleHeaderWheel behavior but for touch gestures
     */
    const handleHeaderTouchStart = useCallback(
      (event) => {
        if (Platform.OS !== "web" || !isMobile) return;
        const touch = event.touches?.[0];
        if (!touch) return;

        const scrollView = getDOMElement(horizontalScrollRef.current);
        touchStartRef.current = { x: touch.clientX, y: touch.clientY };
        touchScrollStartRef.current = scrollView?.scrollLeft ?? 0;
      },
      [getDOMElement, isMobile]
    );

    const handleHeaderTouchMove = useCallback(
      (event) => {
        if (Platform.OS !== "web" || !isMobile) return;
        if (!touchStartRef.current) return;

        const touch = event.touches?.[0];
        if (!touch) return;

        const deltaX = touchStartRef.current.x - touch.clientX;
        const deltaY = touch.clientY - touchStartRef.current.y;

        // Only handle if horizontal movement dominates
        if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 5) {
          event.preventDefault(); // Prevent vertical scroll when dragging horizontally

          const scrollView = getDOMElement(horizontalScrollRef.current);
          if (scrollView) {
            scrollView.scrollLeft = touchScrollStartRef.current + deltaX;
            syncHeaderScroll(scrollView.scrollLeft);
          }
        }
      },
      [getDOMElement, isMobile, syncHeaderScroll]
    );

    const handleHeaderTouchEnd = useCallback(() => {
      touchStartRef.current = null;
      touchScrollStartRef.current = 0;
    }, []);

    // Initialize header transform for GPU acceleration
    useEffect(() => {
      if (Platform.OS !== "web") return;

      const headerContainer = getDOMElement(headerScrollContainerRef.current);
      if (headerContainer && headerContainer.style) {
        // Initialize with translate3d to force GPU layer creation
        headerContainer.style.transform = "translate3d(0, 0, 0)";
      }
    }, [getDOMElement]);

    // Setup wheel event listener on sticky header
    useEffect(() => {
      if (Platform.OS !== "web") return;

      const stickyHeader = getDOMElement(stickyHeaderRef.current);
      if (!stickyHeader) return;

      stickyHeader.addEventListener("wheel", handleHeaderWheel, {
        passive: false,
      });

      return () => {
        stickyHeader.removeEventListener("wheel", handleHeaderWheel);
      };
    }, [getDOMElement, handleHeaderWheel]);

    // Setup touch event listeners on sticky header for mobile horizontal scrolling
    useEffect(() => {
      if (Platform.OS !== "web" || !isMobile) return;

      const stickyHeader = getDOMElement(stickyHeaderRef.current);
      if (!stickyHeader) return;

      stickyHeader.addEventListener("touchstart", handleHeaderTouchStart, {
        passive: true,
      });
      stickyHeader.addEventListener("touchmove", handleHeaderTouchMove, {
        passive: false,
      });
      stickyHeader.addEventListener("touchend", handleHeaderTouchEnd, {
        passive: true,
      });

      return () => {
        stickyHeader.removeEventListener("touchstart", handleHeaderTouchStart);
        stickyHeader.removeEventListener("touchmove", handleHeaderTouchMove);
        stickyHeader.removeEventListener("touchend", handleHeaderTouchEnd);
      };
    }, [
      getDOMElement,
      isMobile,
      handleHeaderTouchStart,
      handleHeaderTouchMove,
      handleHeaderTouchEnd,
    ]);

    // Prevent browser focus/tab from horizontally scrolling the header clip
    useEffect(() => {
      if (Platform.OS !== "web") return;

      const headerClip = getDOMElement(headerClipRef.current);
      if (!headerClip || !headerClip.addEventListener) return;

      const resetHeaderClipScroll = () => {
        if (headerClip.scrollLeft !== 0) {
          headerClip.scrollLeft = 0;
        }
      };

      headerClip.addEventListener("scroll", resetHeaderClipScroll, {
        passive: true,
      });
      headerClip.addEventListener("focusin", resetHeaderClipScroll);

      return () => {
        headerClip.removeEventListener("scroll", resetHeaderClipScroll);
        headerClip.removeEventListener("focusin", resetHeaderClipScroll);
      };
    }, [getDOMElement]);

    /**
     * Callback to receive the ScrollView ref for scroll control and drag auto-scroll
     */
    const handleScrollViewRef = useCallback(
      (scrollViewElement) => {
        horizontalScrollRef.current = scrollViewElement;
        if (scrollViewElement) {
          setHorizontalScrollRef(scrollViewElement);
        }
      },
      [setHorizontalScrollRef]
    );

    const coach = block?.client?.coach ?? block?.coach;
    const shouldShowTutorialLink = !isMobile && showTutorialLink;
    const shouldShowTutorialBanner = !isMobile && showTutorialBanner;
    const shouldShowProtocolsGuide =
      block?.client?.coach?.showingProtocolsGuideBlockView ||
      block?.coach?.showingProtocolsGuideBlockView;
    const showSetPriority = coach?.showSetPriority ?? false;
    const protocolGuideTopPadding = isMobile ? 8 : 16;
    const rirOrRpe = coach?.rirOrRpe ?? false;
    const fadeColors = useMemo(() => {
      return {
        solid: toRgba(Colors.backgroundColor, 0.96),
        transparent: toRgba(Colors.backgroundColor, 0),
      };
    }, []);

    const handleScrollAreaMouseEnter = useCallback(() => {
      if (isMobile) return;
      setIsHoveringScrollArea(true);
    }, [isMobile]);

    const handleScrollAreaMouseLeave = useCallback(() => {
      if (isMobile) return;
      setIsHoveringScrollArea(false);
    }, [isMobile]);

    const handleWeekHeaderMouseEnter = useCallback(() => {
      if (isMobile) return;
      setIsHoveringWeekHeader(true);
    }, [isMobile]);

    const handleWeekHeaderMouseLeave = useCallback(() => {
      if (isMobile) return;
      setIsHoveringWeekHeader(false);
      setHoveredScrollButton(null);
      edgeHoverLockRef.current = { left: false, right: false };
      setEdgeHoverLock(edgeHoverLockRef.current);
    }, [isMobile]);

    const setEdgeHoverLockState = useCallback((nextLock) => {
      edgeHoverLockRef.current = nextLock;
      setEdgeHoverLock(nextLock);
    }, []);

    const clearEdgeHoverLock = useCallback(
      (side) => {
        const currentLock = edgeHoverLockRef.current;
        if (!currentLock[side]) return;
        setEdgeHoverLockState({ ...currentLock, [side]: false });
      },
      [setEdgeHoverLockState]
    );

    const exitWorkoutEditMode = () => {
      block?.clearBlockViewEditingWorkoutId?.();
    };

    useEffect(() => {
      if (Platform.OS !== "web") return;
      if (typeof window === "undefined") return;

      const onKeyDown = (e) => {
        if (e?.key !== "Escape") return;
        const anySelected = !!block?.blockViewEditingWorkoutId;
        if (!anySelected) return;
        exitWorkoutEditMode();
      };

      window.addEventListener("keydown", onKeyDown);
      return () => window.removeEventListener("keydown", onKeyDown);
    }, [block]);

    // Generate weeks array based on block.totalWeeks
    const weeks = Array.from({ length: block.totalWeeks }, (_, i) => i + 1);

    // Calculate current week if block is active
    // Access block properties directly for MobX reactivity
    const currentWeek =
      block?.isActive && block?.currentWeek ? block.currentWeek() : null;

    const clientId =
      block?.client?.id ??
      block?.client?.uid ??
      block?.client?.clientId ??
      null;
    const blockId = block?.id ?? null;
    const scrollCacheKey =
      clientId && blockId ? `${clientId}:${blockId}` : null;

    // Track workouts length to trigger re-renders when workouts are added/deleted
    // This ensures MobX reactivity works properly with the memoized renderWeekColumn
    // Accessing block.workouts.length here ensures MobX tracks it
    const workoutsLength = block.workouts?.length ?? 0;
    // Track week/order changes to re-render only when ordering-related fields change
    const workoutsOrderSignature = (block.workouts || [])
      .map((w) => `${w.id}:${w.week}:${w.order}`)
      .join("|");
    const blockHasProtocolSlots = (block.workouts || []).some(
      (workout) => workout?.hasProtocolSlotExercises
    );
    const showOnlyProtocolSlots =
      coach?.showOnlyProtocolSlots === true && blockHasProtocolSlots;
    const protocolSlotsSignature = showOnlyProtocolSlots
      ? (block.workouts || [])
          .map((w) => (w?.hasProtocolSlotExercises ? "1" : "0"))
          .join("|")
      : "";

    // Memoize getWorkoutsForWeek to keep it stable, but re-create it when order changes
    const getWorkoutsForWeek = useCallback(
      (week) => {
        return block.workouts
          .filter((w) => w.week === week)
          .sort((a, b) => a.order - b.order);
      },
      [block.workouts, workoutsLength, workoutsOrderSignature]
    );

    // Calculate max workout position across all weeks
    const maxWorkoutPosition = useMemo(() => {
      if (!block?.workouts || block.workouts.length === 0) return 0;
      if (!block?.totalWeeks) return 0;

      let max = 0;
      for (let week = 1; week <= block.totalWeeks; week++) {
        const weekWorkouts = getWorkoutsForWeek(week);
        max = Math.max(max, weekWorkouts.length);
      }
      return max;
    }, [
      block?.workouts,
      block?.totalWeeks,
      getWorkoutsForWeek,
      workoutsLength,
    ]);

    // Filter workouts by position using MobX property
    const getFilteredWorkoutsForWeek = useCallback(
      (week) => {
        const weekWorkouts = getWorkoutsForWeek(week);
        if (showOnlyProtocolSlots) {
          return weekWorkouts.filter(
            (workout) => workout?.hasProtocolSlotExercises
          );
        }

        const selectedPositions = block.selectedWorkoutPositions;

        // If null, show all workouts
        if (!selectedPositions) {
          return weekWorkouts;
        }

        // Filter by position (1-based index)
        return weekWorkouts.filter((workout, index) => {
          const position = index + 1;
          return selectedPositions.has(position);
        });
      },
      [
        getWorkoutsForWeek,
        block.selectedWorkoutPositions,
        showOnlyProtocolSlots,
        protocolSlotsSignature,
        workoutsLength,
      ]
    );

    const persistBlockChange = () => {
      if (block.client) {
        block.client.updateClientTransactionData(
          String(block.id).toLowerCase()
        );
        block.client.startOrResetBatchTimer();
      } else if (block.coach) {
        block.coach.updateCoachTransactionBlocks(
          String(block.id).toLowerCase()
        );
        block.coach.startOrResetBatchTimer();
      }
    };

    const handleCopyExercise = (exercise) => {
      if (!exercise) return;
      coach?.copyExercisesFromExercise?.(exercise);
    };

    const handlePasteExercise = (workout) => {
      if (!workout) return;
      const payloads = Array.isArray(coach?.copiedExercises)
        ? coach.copiedExercises
        : [];
      if (payloads.length === 0) return;

      // Check if pasting would exceed exercise limit
      const currentExerciseCount = workout?.exercises?.length ?? 0;
      const exercisesToPaste = payloads.length;
      if (
        currentExerciseCount + exercisesToPaste >
        WorkoutLimits.MAX_EXERCISES_PER_WORKOUT
      ) {
        showToast("maxExercisesPerWorkoutReached-string", {
          type: "warning",
          title: "maxExercisesPerWorkoutReached-string",
          description: "maxExercisesPerWorkoutDescription-string",
          variables: { limit: WorkoutLimits.MAX_EXERCISES_PER_WORKOUT },
        });
        return;
      }

      const beforeExerciseCount = workout?.exercises?.length ?? 0;
      workout?.pasteExercisesFromClipboardPayloads?.(payloads);
      const afterExercises = Array.isArray(workout?.exercises)
        ? workout.exercises
        : [];
      const addedExercises = afterExercises.slice(beforeExerciseCount);
      const hasNonSlotExercise = addedExercises.some(
        (exercise) => exercise?.protocolSlot == null
      );
      if (coach?.showOnlyProtocolSlots && hasNonSlotExercise) {
        coach.showOnlyProtocolSlots = false;
      }
      // Set workout to edit mode when pasting exercises
      block?.setBlockViewEditingWorkoutId?.(workout.id);
      persistBlockChange();

      // Show toast notification
      const getWorkoutName = (w) => {
        if (!w || !block?.workouts) return "";
        const week = w?.week ?? 1;
        const sameWeekWorkouts = block.workouts
          .filter((wk) => wk.week === week)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        const workoutIndex = sameWeekWorkouts.findIndex((wk) => wk.id === w.id);
        const workoutNumber =
          workoutIndex > -1 ? workoutIndex + 1 : (w?.order ?? 0) + 1;
        const workoutName = typeof w?.name === "string" ? w.name.trim() : "";
        return (
          workoutName ||
          t("workoutWithNumber-string", { number: workoutNumber })
        );
      };
      const workoutName = getWorkoutName(workout);
      showToast("exercisesPasted-string", {
        type: "info",
        title: "exercisesPasted-string",
        description: "exercisesPastedDescription-string",
        duration: BlockPlanificationConstants.TOAST_DURATION_MS,
        variables: { workoutName },
      });
    };

    const handleDuplicateWorkout = (workout) => {
      if (!workout) return;

      const weekWorkouts = getWorkoutsForWeek(workout.week);
      const newOrder =
        weekWorkouts.length > 0
          ? Math.max(...weekWorkouts.map((w) => w.order)) + 1
          : 0;

      const newWorkout = new Workout(
        uuidv4(),
        workout?.date ?? null,
        workout?.name ?? "",
        newOrder,
        workout.week,
        false,
        block,
        []
      );
      newWorkout.setProperty("hasChanged", true);

      if (Array.isArray(workout?.exercises)) {
        workout.exercises.forEach((exercise) => {
          if (!exercise) return;
          newWorkout.copyExercise(exercise);
        });
      }

      block.workouts.push(newWorkout);
      persistBlockChange();
    };

    const handleDeleteWorkout = (workout) => {
      if (!workout) return;
      block.deleteWorkoutWithoutReordering(workout);
    };

    /**
     * Handle reordering exercises within the same workout
     * Called from ExerciseDndProvider when an exercise is dropped within its workout
     */
    const handleMoveExercise = useCallback(
      (workoutId, fromIndex, toIndex, exerciseId) => {
        if (!block?.workouts) return;
        if (fromIndex === toIndex) return;

        const workout = block.workouts.find((w) => w.id === workoutId);
        if (!workout) return;

        if (workout.reorderExercise) {
          workout.reorderExercise(fromIndex, toIndex);
        }
        persistBlockChange();

        const movedExercise = workout.exercises?.find(
          (ex) => ex?.id === exerciseId
        );
        if (movedExercise) {
          const position = toIndex + 1;
          const exerciseName =
            movedExercise?.getName?.(t, i18n.language) ||
            movedExercise?.name ||
            t("exercise-string");
          showToast("exerciseMovedToastTitle-string", {
            type: "info",
            title: "exerciseMovedToastTitle-string",
            description: "exerciseMovedToastDescriptionSameWorkout-string",
            duration: BlockPlanificationConstants.TOAST_DURATION_MS,
            variables: { exerciseName, position },
          });
        }

        // Return moved exercise ID for animation
        return exerciseId;
      },
      [block, persistBlockChange, showToast, t, i18n.language]
    );

    /**
     * Handle moving exercises between workouts
     * Called from ExerciseDndProvider when an exercise is dropped in a different workout
     */
    const handleMoveExerciseCrossWorkout = useCallback(
      (sourceWorkoutId, exerciseId, targetWorkoutId, targetIndex) => {
        if (!block?.workouts) return;
        if (sourceWorkoutId === targetWorkoutId) return;

        const sourceWorkout = block.workouts.find(
          (w) => w.id === sourceWorkoutId
        );
        if (!sourceWorkout) return;

        const targetWorkout = block.workouts.find(
          (w) => w.id === targetWorkoutId
        );
        if (!targetWorkout) return;

        const sourceExercise = sourceWorkout.exercises?.find(
          (ex) => ex?.id === exerciseId
        );
        if (!sourceExercise) return;

        // Check if this is a protocol slot exercise (may move as pack)
        const protocolSlot =
          typeof sourceExercise.protocolSlot === "string" &&
          sourceExercise.protocolSlot.trim()
            ? sourceExercise.protocolSlot.trim()
            : null;

        const pack =
          protocolSlot && Array.isArray(sourceWorkout.exercises)
            ? sourceWorkout.exercises.filter(
                (ex) => ex?.protocolSlot === protocolSlot
              )
            : [sourceExercise];

        const pairedExercise =
          pack.find((ex) => ex?.id !== sourceExercise?.id) ?? null;

        // Check if target workout would exceed exercise limit
        const targetExerciseCount = targetWorkout?.exercises?.length ?? 0;
        const exercisesToAdd = pack.length;
        if (
          targetExerciseCount + exercisesToAdd >
          WorkoutLimits.MAX_EXERCISES_PER_WORKOUT
        ) {
          showToast("maxExercisesPerWorkoutReached-string", {
            type: "warning",
            title: "maxExercisesPerWorkoutReached-string",
            description: "maxExercisesPerWorkoutDescription-string",
            variables: { limit: WorkoutLimits.MAX_EXERCISES_PER_WORKOUT },
          });
          return;
        }

        // Track moved exercise IDs for animation
        const movedExerciseIds = pack.map((ex) => ex?.id).filter(Boolean);

        // Move exercise(s)
        if (pack.length > 1 && sourceWorkout.removeExercisesByIds) {
          const removed = sourceWorkout.removeExercisesByIds(
            pack.map((ex) => ex?.id)
          );
          if (!removed.length) return movedExerciseIds;
          if (targetWorkout.receiveExercisePack) {
            targetWorkout.receiveExercisePack(removed, targetIndex);
          } else {
            removed.forEach((exercise, idx) => {
              targetWorkout.receiveExercise(exercise, targetIndex + idx);
            });
          }
        } else {
          const exercise = sourceWorkout.removeExercise(exerciseId);
          if (!exercise) return movedExerciseIds;
          targetWorkout.receiveExercise(exercise, targetIndex);
        }

        persistBlockChange();

        // Helper functions for toast notification
        const getExerciseName = (ex) =>
          ex?.getName?.(t, i18n.language) || ex?.name || t("exercise-string");
        const getWorkoutName = (w) => {
          if (!w || !block?.workouts) return "";
          const week = w?.week ?? 1;
          const sameWeekWorkouts = block.workouts
            .filter((wk) => wk.week === week)
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
          const workoutIndex = sameWeekWorkouts.findIndex(
            (wk) => wk.id === w.id
          );
          const workoutNumber =
            workoutIndex > -1 ? workoutIndex + 1 : (w?.order ?? 0) + 1;
          const workoutName = typeof w?.name === "string" ? w.name.trim() : "";
          return (
            workoutName ||
            t("workoutWithNumber-string", { number: workoutNumber })
          );
        };
        const getWeekLabel = (week) => {
          if (!week && week !== 0) return "";
          const customWeekName = block?.getWeekName?.(week) || "";
          return customWeekName.trim() !== ""
            ? customWeekName
            : t("weekWithNumber-string", { week });
        };

        // Show toast notification
        const isSameWeek = sourceWorkout?.week === targetWorkout?.week;

        if (pairedExercise?.name) {
          showToast("exerciseMovedToastTitlePlural-string", {
            type: "info",
            title: "exerciseMovedToastTitlePlural-string",
            description: isSameWeek
              ? "exerciseMovedToastDescriptionPluralSameWeek-string"
              : "exerciseMovedToastDescriptionPluralCrossWeek-string",
            duration: BlockPlanificationConstants.TOAST_DURATION_MS,
            variables: {
              firstExerciseName: getExerciseName(sourceExercise),
              secondExerciseName: getExerciseName(pairedExercise),
              workoutName: getWorkoutName(targetWorkout),
              weekLabel: getWeekLabel(targetWorkout?.week),
              firstPosition: targetIndex + 1,
              secondPosition: targetIndex + 2,
            },
          });
        } else {
          showToast("exerciseMovedToastTitle-string", {
            type: "info",
            title: "exerciseMovedToastTitle-string",
            description: isSameWeek
              ? "exerciseMovedToastDescriptionSameWeek-string"
              : "exerciseMovedToastDescriptionCrossWeek-string",
            duration: BlockPlanificationConstants.TOAST_DURATION_MS,
            variables: {
              exerciseName: getExerciseName(sourceExercise),
              workoutName: getWorkoutName(targetWorkout),
              weekLabel: getWeekLabel(targetWorkout?.week),
              position: targetIndex + 1,
            },
          });
        }

        // Return moved exercise IDs for animation
        return movedExerciseIds;
      },
      [block, persistBlockChange, showToast, t, i18n.language]
    );

    const handleAddWorkout = (week) => {
      const weekWorkouts = getWorkoutsForWeek(week);

      // Check workout per week limit
      if (weekWorkouts.length >= WorkoutLimits.MAX_WORKOUTS_PER_WEEK) {
        showToast("maxWorkoutsPerWeekReached-string", {
          type: "warning",
          title: "maxWorkoutsPerWeekReached-string",
          description: "maxWorkoutsPerWeekDescription-string",
          variables: { limit: WorkoutLimits.MAX_WORKOUTS_PER_WEEK },
        });
        return;
      }

      const newOrder =
        weekWorkouts.length > 0
          ? Math.max(...weekWorkouts.map((w) => w.order)) + 1
          : 0;

      const newWorkout = new Workout(
        uuidv4(),
        null,
        i18n.language == "en"
          ? `Day ${String(weekWorkouts.length + 1)}`
          : `Día ${String(weekWorkouts.length + 1)}`,
        newOrder,
        week,
        true,
        block,
        []
      );
      newWorkout.setProperty("hasChanged", true);
      block.workouts.push(newWorkout);
      if (coach?.showOnlyProtocolSlots) {
        coach.showOnlyProtocolSlots = false;
      }

      // Calculate the new workout's position (1-based index after sorting)
      const newPosition = weekWorkouts.length + 1;

      // If filter is active, add new position to filter so it becomes visible
      if (block.selectedWorkoutPositions !== null) {
        const newSet = new Set(block.selectedWorkoutPositions);
        newSet.add(newPosition);
        block.selectedWorkoutPositions = newSet;
      }

      persistBlockChange();
    };

    const confirmDeleteWeek = () => {
      if (weekToDelete) {
        block.deleteWeekWorkoutsWithoutReordering(weekToDelete);
        setWeekToDelete(null);
        setShowingDeleteWeekAlert(false);
      }
    };

    const onDeleteWeekPress = (week) => {
      setWeekToDelete(week);
      setShowingDeleteWeekAlert(true);
    };

    const onDuplicateWeekPress = (week) => {
      setWeekToDuplicate(week);
      setShowingDuplicateWeek(true);
    };

    const getWeekLabel = (week) => {
      const custom = block?.getWeekName?.(week);
      if (typeof custom === "string" && custom.trim() !== "") return custom;
      return t("weekWithNumber-string", { week });
    };

    const startEditingWeek = (week) => {
      setEditingWeek(week);
    };

    const commitWeekName = (week, draft) => {
      if (typeof week !== "number") return;
      const next = typeof draft === "string" ? draft.trim() : "";
      block?.setWeekName?.(week, next);
      setEditingWeek((prev) => (prev === week ? null : prev));
    };

    const cancelWeekNameEdit = () => {
      setEditingWeek(null);
    };

    const coerceToDate = (value) => {
      if (!value) return null;
      if (value instanceof Date) return value;
      if (typeof value?.toDate === "function") {
        try {
          return value.toDate();
        } catch (e) {
          return null;
        }
      }
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return null;
      return d;
    };

    const formatWorkoutDate = (value) => {
      const d = coerceToDate(value);
      if (!d) return null;
      const locale = i18n?.language || "en";

      const month = (() => {
        try {
          return new Intl.DateTimeFormat(locale, { month: "short" }).format(d);
        } catch (e) {
          return d.toLocaleDateString();
        }
      })();

      try {
        // Format: "Dec 1"
        return `${month} ${d.getDate()}`;
      } catch (e) {
        return d.toLocaleDateString();
      }
    };

    const formatWeekInterval = (week) => {
      const locale = i18n?.language || "en";
      const workouts = getWorkoutsForWeek(week);
      const dates = workouts
        .map((w) => coerceToDate(w?.date))
        .filter((d) => d instanceof Date)
        .sort((a, b) => a.getTime() - b.getTime());

      if (dates.length === 0) return null;

      const start = dates[0];
      const end = dates[dates.length - 1];

      const formatMonth = (d) => {
        try {
          return new Intl.DateTimeFormat(locale, { month: "short" }).format(d);
        } catch (e) {
          return d.toLocaleDateString();
        }
      };

      const startMonth = formatMonth(start);
      const endMonth = formatMonth(end);
      const startDay = start.getDate();
      const endDay = end.getDate();

      if (start.toDateString() === end.toDateString()) {
        // Format: "Dec 1"
        return `${startMonth} ${startDay}`;
      }

      if (
        start.getFullYear() === end.getFullYear() &&
        start.getMonth() === end.getMonth()
      ) {
        // Same month: "Dec 1 - 4"
        return `${startMonth} ${startDay} - ${endDay}`;
      }

      // Different month (or year): "Nov 24 - Dec 2"
      return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
    };

    // Calculate column width based on showSetPriority and mobile
    const baseColumnWidth = isMobile
      ? BlockPlanificationConstants.MOBILE_COLUMN_WIDTH
      : BlockPlanificationConstants.COLUMN_WIDTH;
    const columnWidth = showSetPriority
      ? baseColumnWidth + (isMobile ? 28 : 32)
      : baseColumnWidth;
    const totalColumnWidth =
      columnWidth + BlockPlanificationConstants.COLUMN_GAP;
    const totalWidth = useMemo(
      () =>
        weeks.length > 0
          ? weeks.length * totalColumnWidth -
            BlockPlanificationConstants.COLUMN_GAP
          : 0,
      [weeks.length, totalColumnWidth]
    );

    const updateScrollIndicators = useCallback(
      (nextScrollLeft, nextContainerWidth) => {
        const scrollLeft =
          typeof nextScrollLeft === "number"
            ? nextScrollLeft
            : scrollMetricsRef.current.scrollLeft;
        const containerWidth =
          typeof nextContainerWidth === "number"
            ? nextContainerWidth
            : scrollMetricsRef.current.containerWidth;

        scrollMetricsRef.current = { scrollLeft, containerWidth };

        const maxScrollLeft = Math.max(0, totalWidth - containerWidth);
        const canScrollLeft = scrollLeft > 4;
        const canScrollRight = scrollLeft < maxScrollLeft - 4;

        const prev = scrollIndicatorsRef.current;
        if (
          prev.canScrollLeft !== canScrollLeft ||
          prev.canScrollRight !== canScrollRight
        ) {
          const next = { canScrollLeft, canScrollRight };
          scrollIndicatorsRef.current = next;
          setScrollIndicators(next);
        }

        const prevLock = edgeHoverLockRef.current;
        const nextLock = {
          left: canScrollLeft
            ? false
            : hoveredScrollButton === "left"
            ? true
            : prevLock.left,
          right: canScrollRight
            ? false
            : hoveredScrollButton === "right"
            ? true
            : prevLock.right,
        };

        if (
          prevLock.left !== nextLock.left ||
          prevLock.right !== nextLock.right
        ) {
          setEdgeHoverLockState(nextLock);
        }
      },
      [totalWidth, hoveredScrollButton, setEdgeHoverLockState]
    );

    useEffect(() => {
      didInitScrollRef.current = false;
      didInitFullRenderRef.current = false;
      setInitialFullRenderWeek(null);
      exerciseHistoryStore.reset();
    }, [block?.id]);

    useEffect(() => {
      if (!showExerciseHistory) {
        exerciseHistoryStore.reset();
      }
    }, [showExerciseHistory]);

    useEffect(() => {
      let isMounted = true;
      setCachedScrollLeft(null);
      setIsCacheLoaded(false);

      if (!scrollCacheKey) {
        setIsCacheLoaded(true);
        return () => {};
      }

      getScrollPosition(scrollCacheKey).then((value) => {
        if (!isMounted) return;
        setCachedScrollLeft(value);
        setIsCacheLoaded(true);
      });

      return () => {
        isMounted = false;
      };
    }, [scrollCacheKey]);

    const applyInitialScroll = useCallback(() => {
      if (didInitScrollRef.current) return;
      if (!isCacheLoaded) return;

      const scrollView = getDOMElement(horizontalScrollRef.current);
      if (!scrollView) return;

      const containerWidth = scrollMetricsRef.current.containerWidth;
      if (!containerWidth) return;

      const maxScrollLeft = Math.max(0, totalWidth - containerWidth);
      let targetScrollLeft = 0;

      if (Number.isFinite(cachedScrollLeft)) {
        targetScrollLeft = cachedScrollLeft;
      } else if (
        block?.isActive &&
        typeof currentWeek === "number" &&
        currentWeek >= 3
      ) {
        // On mobile, initialize to current week; on desktop, initialize to previous week
        targetScrollLeft = isMobile
          ? (currentWeek - 1) * totalColumnWidth
          : (currentWeek - 2) * totalColumnWidth;
      }

      targetScrollLeft = Math.max(0, Math.min(maxScrollLeft, targetScrollLeft));
      scrollView.scrollLeft = targetScrollLeft;

      syncHeaderScroll(targetScrollLeft);
      updateScrollIndicators(targetScrollLeft, containerWidth);
      latestScrollLeftRef.current = targetScrollLeft;

      if (!didInitFullRenderRef.current && block?.totalWeeks > 0) {
        const initialIndex = Math.floor(targetScrollLeft / totalColumnWidth);
        const weekNumber = Math.min(
          block.totalWeeks,
          Math.max(1, initialIndex + 1)
        );
        if (Number.isFinite(weekNumber)) {
          setInitialFullRenderWeek(weekNumber);
          didInitFullRenderRef.current = true;
        }
      }

      didInitScrollRef.current = true;
    }, [
      block?.isActive,
      block?.totalWeeks,
      cachedScrollLeft,
      currentWeek,
      getDOMElement,
      isCacheLoaded,
      isMobile,
      syncHeaderScroll,
      totalColumnWidth,
      totalWidth,
      updateScrollIndicators,
    ]);

    useEffect(() => {
      applyInitialScroll();
    }, [applyInitialScroll]);

    const handleContainerWidthChange = useCallback(
      (nextWidth) => {
        updateScrollIndicators(scrollMetricsRef.current.scrollLeft, nextWidth);
        applyInitialScroll();
      },
      [updateScrollIndicators, applyInitialScroll]
    );

    /**
     * Handle scroll events from the virtualized list
     */
    const handleVirtualScroll = useCallback(
      (scrollOffset) => {
        syncHeaderScroll(scrollOffset);
        updateScrollIndicators(scrollOffset);
        latestScrollLeftRef.current = scrollOffset;
      },
      [syncHeaderScroll, updateScrollIndicators]
    );

    useEffect(() => {
      updateScrollIndicators(
        scrollMetricsRef.current.scrollLeft,
        scrollMetricsRef.current.containerWidth
      );
    }, [totalWidth, updateScrollIndicators]);

    const nudgeHorizontalScroll = useCallback(
      (direction) => {
        const scrollView = getDOMElement(horizontalScrollRef.current);
        if (!scrollView) return;

        const step = columnWidth + BlockPlanificationConstants.COLUMN_GAP;
        const maxScrollLeft = Math.max(
          0,
          totalWidth - scrollMetricsRef.current.containerWidth
        );
        const current =
          scrollView.scrollLeft ?? scrollMetricsRef.current.scrollLeft;
        const next = Math.min(
          maxScrollLeft,
          Math.max(0, current + direction * step)
        );

        scrollView.scrollLeft = next;

        syncHeaderScroll(next);
        updateScrollIndicators(next, scrollMetricsRef.current.containerWidth);
        latestScrollLeftRef.current = next;
      },
      [
        getDOMElement,
        columnWidth,
        totalWidth,
        syncHeaderScroll,
        updateScrollIndicators,
      ]
    );

    useEffect(() => {
      return () => {
        if (!scrollCacheKey) return;
        const scrollView = getDOMElement(horizontalScrollRef.current);
        const scrollLeft =
          scrollView?.scrollLeft ?? latestScrollLeftRef.current ?? 0;
        if (Number.isFinite(scrollLeft)) {
          setScrollPosition(scrollCacheKey, scrollLeft);
        }
      };
    }, [getDOMElement, scrollCacheKey]);

    const showScrollControls = !isMobile && isHoveringWeekHeader;
    const isHoveringScrollButtons = hoveredScrollButton !== null;
    const shouldShowWideFade = isHoveringWeekHeader;
    const showLeftFade = scrollIndicators.canScrollLeft || edgeHoverLock.left;
    const showRightFade =
      scrollIndicators.canScrollRight || edgeHoverLock.right;

    const recomputeMaxWeekHeight = useCallback(() => {
      let nextMax = 0;
      weekHeightCacheRef.current.forEach((height) => {
        if (height > nextMax) {
          nextMax = height;
        }
      });
      return nextMax;
    }, []);

    const updateWeekHeight = useCallback(
      (week, height) => {
        if (!Number.isFinite(height) || height <= 0) return;
        const cache = weekHeightCacheRef.current;
        const previous = cache.get(week);
        if (previous !== undefined && Math.abs(previous - height) <= 2) {
          return;
        }
        cache.set(week, height);

        const nextMax = recomputeMaxWeekHeight();
        setMaxCachedWeekHeight((prevMax) =>
          prevMax !== nextMax ? nextMax : prevMax
        );

        if (initialFullRenderWeek === week) {
          setInitialFullRenderWeek(null);
        }
      },
      [initialFullRenderWeek, recomputeMaxWeekHeight]
    );

    useEffect(() => {
      const totalWeeks = block?.totalWeeks ?? 0;
      const cache = weekHeightCacheRef.current;
      let didChange = false;
      cache.forEach((_, week) => {
        if (week > totalWeeks) {
          cache.delete(week);
          didChange = true;
        }
      });
      if (didChange) {
        const nextMax = recomputeMaxWeekHeight();
        setMaxCachedWeekHeight((prevMax) =>
          prevMax !== nextMax ? nextMax : prevMax
        );
      }
    }, [block?.totalWeeks, recomputeMaxWeekHeight]);

    useEffect(() => {
      const cache = weekHeightCacheRef.current;
      const weeksWithWorkouts = new Set();
      (block?.workouts || []).forEach((workout) => {
        if (workout?.week) {
          weeksWithWorkouts.add(workout.week);
        }
      });

      let didChange = false;
      cache.forEach((_, week) => {
        if (!weeksWithWorkouts.has(week)) {
          cache.delete(week);
          didChange = true;
        }
      });

      if (didChange) {
        const nextMax = recomputeMaxWeekHeight();
        setMaxCachedWeekHeight((prevMax) =>
          prevMax !== nextMax ? nextMax : prevMax
        );
      }
    }, [
      block?.workouts,
      workoutsLength,
      workoutsOrderSignature,
      recomputeMaxWeekHeight,
    ]);

    /**
     * Memoized render function for each week column
     * This is passed to VirtualizedWeekColumns and called for visible columns only
     * workoutsLength dependency ensures re-render when workouts are added/deleted
     */
    const renderWeekColumn = useCallback(
      (week, index) => {
        const weekWorkouts = getFilteredWorkoutsForWeek(week);
        const isWeekHidden = block.isWeekHidden
          ? block.isWeekHidden(week)
          : false;

        return (
          <WeekColumnContainer
            week={week}
            hasWorkouts={weekWorkouts.length > 0}
            columnWidth={columnWidth}
            getDOMElement={getDOMElement}
            onHeightChange={updateWeekHeight}
          >
            {/* Workouts list */}
            <View style={styles.workoutsList}>
              {weekWorkouts.map((workout) => (
                <LazyWorkoutCard
                  key={workout.id}
                  workout={workout}
                  block={block}
                  coach={coach}
                  t={t}
                  formatWorkoutDate={formatWorkoutDate}
                  handleDuplicateWorkout={handleDuplicateWorkout}
                  exitWorkoutEditMode={exitWorkoutEditMode}
                  handleCopyExercise={handleCopyExercise}
                  handlePasteExercise={handlePasteExercise}
                  handleDeleteWorkout={handleDeleteWorkout}
                  persistBlockChange={persistBlockChange}
                  showSetPriority={showSetPriority}
                  rirOrRpe={rirOrRpe}
                  isWeekHidden={isWeekHidden}
                  forceRender={week === initialFullRenderWeek}
                  exercisesExpanded={block?.exercisesExpanded ?? true}
                  showOnlyProtocolSlots={showOnlyProtocolSlots}
                  showExerciseHistory={showExerciseHistory}
                  columnWidth={columnWidth}
                />
              ))}
            </View>

            {/* Add workout button */}
            <TouchableOpacity
              style={[
                styles.addWorkoutButton,
                weekWorkouts.length > 0 && { marginTop: 8 },
              ]}
              onPress={() => handleAddWorkout(week)}
              onMouseEnter={
                !isMobile ? () => setHoveredAddWorkoutWeek(week) : undefined
              }
              onMouseLeave={
                !isMobile ? () => setHoveredAddWorkoutWeek(null) : undefined
              }
            >
              {/* <Ionicons name="add" size={16} color={Colors.grayColor} /> */}
              <Text
                style={[
                  styles.addWorkoutText,
                  hoveredAddWorkoutWeek === week && {
                    color: Colors.darkgrayColor,
                  },
                ]}
              >
                {"+ "}
                {t("addWorkout-string")}
              </Text>
            </TouchableOpacity>
          </WeekColumnContainer>
        );
      },
      [
        block,
        coach,
        t,
        formatWorkoutDate,
        handleDuplicateWorkout,
        exitWorkoutEditMode,
        handleCopyExercise,
        handlePasteExercise,
        handleDeleteWorkout,
        persistBlockChange,
        handleAddWorkout,
        getFilteredWorkoutsForWeek,
        workoutsLength, // Track workouts length to trigger re-render on add/delete
        showSetPriority,
        rirOrRpe,
        columnWidth,
        hoveredAddWorkoutWeek,
        isMobile,
        updateWeekHeight,
        block?.exercisesExpanded, // Include exercisesExpanded in dependencies
      ]
    );

    return (
      <View
        style={styles.container}
        onMouseEnter={!isMobile ? handleScrollAreaMouseEnter : undefined}
        onMouseLeave={!isMobile ? handleScrollAreaMouseLeave : undefined}
      >
        {shouldShowTutorialLink && !shouldShowTutorialBanner && (
          <View style={styles.topBannerContainer}>
            <TutorialLinkSection
              text="tutorialProgramWorkouts-string"
              urlPath={tutorialUrlPath}
              marginTop={0}
              marginBottom={16}
              showCloseButton={true}
              onClose={onCloseTutorialLink}
            />
          </View>
        )}
        {shouldShowTutorialBanner && (
          <View style={styles.topBannerContainer}>
            <BlockViewTutorialBanner
              urlPath={tutorialUrlPath}
              onSendFeedback={onSendFeedback}
              onClose={onCloseTutorialBanner}
              onAccessOld={onAccessOld}
              marginBottom={shouldShowProtocolsGuide ? 16 : 12}
              marginTop={8}
            />
          </View>
        )}
        {/* Sticky header section - contains protocols guide (if shown) and week headers */}
        <View
          ref={stickyHeaderRef}
          style={[
            styles.stickyHeaderContainer,
            shouldShowProtocolsGuide && {
              marginTop: -protocolGuideTopPadding,
              paddingTop: protocolGuideTopPadding,
            },
          ]}
        >
          {/* Protocol info section - sticky above week headers */}
          {shouldShowProtocolsGuide && (
            <View style={styles.protocolsGuideContainer}>
              <ProtocolInfoSection
                coach={coach}
                animated={true}
                onClose={() =>
                  coach?.setProperty?.("showingProtocolsGuideBlockView", false)
                }
              />
            </View>
          )}

          {/* Clip container to hide overflow from translated headers */}
          <View
            ref={headerClipRef}
            style={styles.stickyHeaderClip}
            // On mobile, "box-none" lets touches pass through to content below for natural scrolling
            // while still allowing children (buttons) to receive touch events
            pointerEvents={isMobile ? "box-none" : "auto"}
            onMouseEnter={!isMobile ? handleWeekHeaderMouseEnter : undefined}
            onMouseLeave={!isMobile ? handleWeekHeaderMouseLeave : undefined}
          >
            {/* Inner container that gets translated to match horizontal scroll */}
            <View
              ref={headerScrollContainerRef}
              style={styles.headerColumnsContainer}
            >
              {weeks.map((week, index) => (
                <WeekHeader
                  key={week}
                  week={week}
                  editingWeek={editingWeek}
                  initialWeekName={block?.getWeekName?.(week) ?? ""}
                  getWeekLabel={getWeekLabel}
                  commitWeekName={commitWeekName}
                  cancelWeekNameEdit={cancelWeekNameEdit}
                  startEditingWeek={startEditingWeek}
                  onDeleteWeekPress={onDeleteWeekPress}
                  formatWeekInterval={formatWeekInterval}
                  isWeekHidden={
                    block.isWeekHidden ? block.isWeekHidden(week) : false
                  }
                  toggleHiddenWeek={() =>
                    block.toggleHiddenWeek && block.toggleHiddenWeek(week)
                  }
                  onDuplicateWeekPress={onDuplicateWeekPress}
                  hasWorkouts={getWorkoutsForWeek(week).length > 0}
                  isCurrentWeek={currentWeek !== null && week === currentWeek}
                  t={t}
                  columnWidth={columnWidth}
                  isLastWeek={index === weeks.length - 1}
                  onConfigureWeeks={() => {
                    // Scroll to top if not already at the top
                    const { domElement, scrollViewRef } = findScrollContainer();

                    if (domElement) {
                      const currentScrollTop = domElement.scrollTop || 0;

                      if (currentScrollTop > 0) {
                        // For iOS mobile, use a more robust scrolling approach
                        if (isMobile && isIOS) {
                          // Try React Native ScrollView method first (most reliable on iOS)
                          if (
                            scrollViewRef &&
                            typeof scrollViewRef.scrollTo === "function"
                          ) {
                            try {
                              scrollViewRef.scrollTo({ y: 0, animated: false });
                            } catch (e) {
                              // Fallback if scrollTo fails
                            }
                          }

                          // Aggressively clamp the DOM element (iOS needs multiple attempts)
                          const clampTop = () => {
                            if (domElement) {
                              // Try multiple methods to ensure scroll happens
                              if (typeof domElement.scrollTo === "function") {
                                try {
                                  domElement.scrollTo({
                                    top: 0,
                                    behavior: "auto",
                                  });
                                } catch (e) {
                                  // Ignore errors
                                }
                              }
                              // Direct assignment (most reliable)
                              domElement.scrollTop = 0;

                              // Also try scrollIntoView as fallback
                              try {
                                if (domElement.scrollIntoView) {
                                  domElement.scrollIntoView({
                                    behavior: "auto",
                                    block: "start",
                                  });
                                }
                              } catch (e) {
                                // Ignore errors
                              }
                            }
                          };

                          // Execute immediately and in next frame to ensure it sticks
                          clampTop();
                          requestAnimationFrame(() => {
                            clampTop();
                            // Double-check after a short delay
                            setTimeout(() => {
                              if (domElement && domElement.scrollTop > 0) {
                                domElement.scrollTop = 0;
                              }
                            }, 50);
                          });
                        } else {
                          // For non-iOS, use smooth scroll if supported
                          if (typeof domElement.scrollTo === "function") {
                            domElement.scrollTo({ top: 0, behavior: "smooth" });
                          } else {
                            domElement.scrollTop = 0;
                          }
                        }
                      }
                    }
                    block.setProperty("isShowingDashboard", true);
                  }}
                />
              ))}
            </View>
            <View pointerEvents="box-none" style={styles.headerScrollOverlay}>
              {showLeftFade && (
                <LinearGradient
                  pointerEvents="none"
                  colors={
                    shouldShowWideFade
                      ? [
                          fadeColors.solid,
                          fadeColors.solid,
                          fadeColors.transparent,
                        ]
                      : [fadeColors.solid, fadeColors.transparent]
                  }
                  locations={shouldShowWideFade ? [0, 0.55, 1] : undefined}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                  style={[
                    styles.edgeFade,
                    styles.edgeFadeLeft,
                    shouldShowWideFade && styles.edgeFadeWide,
                  ]}
                />
              )}
              {showRightFade && (
                <LinearGradient
                  pointerEvents="none"
                  colors={
                    shouldShowWideFade
                      ? [
                          fadeColors.solid,
                          fadeColors.solid,
                          fadeColors.transparent,
                        ]
                      : [fadeColors.solid, fadeColors.transparent]
                  }
                  locations={shouldShowWideFade ? [0, 0.55, 1] : undefined}
                  start={{ x: 1, y: 0 }}
                  end={{ x: 0, y: 0 }}
                  style={[
                    styles.edgeFade,
                    styles.edgeFadeRight,
                    shouldShowWideFade && styles.edgeFadeWide,
                  ]}
                />
              )}
              {edgeHoverLock.left && (
                <View
                  pointerEvents="auto"
                  style={[
                    styles.edgeBlocker,
                    styles.edgeBlockerLeft,
                    shouldShowWideFade && styles.edgeFadeWide,
                  ]}
                  onMouseLeave={
                    !isMobile
                      ? () => {
                          setHoveredScrollButton(null);
                          clearEdgeHoverLock("left");
                        }
                      : undefined
                  }
                />
              )}
              {edgeHoverLock.right && (
                <View
                  pointerEvents="auto"
                  style={[
                    styles.edgeBlocker,
                    styles.edgeBlockerRight,
                    shouldShowWideFade && styles.edgeFadeWide,
                  ]}
                  onMouseLeave={
                    !isMobile
                      ? () => {
                          setHoveredScrollButton(null);
                          clearEdgeHoverLock("right");
                        }
                      : undefined
                  }
                />
              )}
              {scrollIndicators.canScrollLeft && showScrollControls && (
                <Tooltip
                  content="horizontalScrollHint-string"
                  placement="top"
                  delayedOpen
                >
                  <HoverableTouchableOpacity
                    style={[styles.scrollButton, styles.scrollButtonLeft]}
                    onPress={() => nudgeHorizontalScroll(-1)}
                    activeOpacity={0.8}
                    hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                    onMouseEnter={
                      !isMobile
                        ? () => setHoveredScrollButton("left")
                        : undefined
                    }
                    onMouseLeave={
                      !isMobile ? () => setHoveredScrollButton(null) : undefined
                    }
                  >
                    <Ionicons
                      name="chevron-back"
                      size={18}
                      color={
                        hoveredScrollButton === "left"
                          ? Colors.darkgrayColor
                          : Colors.grayColor
                      }
                    />
                  </HoverableTouchableOpacity>
                </Tooltip>
              )}
              {scrollIndicators.canScrollRight && showScrollControls && (
                <Tooltip
                  content="horizontalScrollHint-string"
                  placement="top"
                  delayedOpen
                >
                  <HoverableTouchableOpacity
                    style={[styles.scrollButton, styles.scrollButtonRight]}
                    onPress={() => nudgeHorizontalScroll(1)}
                    activeOpacity={0.8}
                    hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                    onMouseEnter={
                      !isMobile
                        ? () => setHoveredScrollButton("right")
                        : undefined
                    }
                    onMouseLeave={
                      !isMobile ? () => setHoveredScrollButton(null) : undefined
                    }
                  >
                    <Ionicons
                      name="chevron-forward"
                      size={18}
                      color={
                        hoveredScrollButton === "right"
                          ? Colors.darkgrayColor
                          : Colors.grayColor
                      }
                    />
                  </HoverableTouchableOpacity>
                </Tooltip>
              )}
            </View>
          </View>
        </View>

        {/* Virtualized horizontal scrollable content wrapped in exercise DnD context */}
        <ExerciseDndProvider
          onMoveExercise={handleMoveExercise}
          onMoveExerciseCrossWorkout={handleMoveExerciseCrossWorkout}
        >
          <VirtualizedWeekColumns
            weeks={weeks}
            renderColumn={renderWeekColumn}
            onScroll={handleVirtualScroll}
            setScrollViewRef={handleScrollViewRef}
            onContainerWidthChange={handleContainerWidthChange}
            style={styles.virtualizedContainer}
            columnWidth={columnWidth}
            minContentHeight={maxCachedWeekHeight}
          />
        </ExerciseDndProvider>

        {showExerciseHistory && <GlobalExerciseHistoryFloating />}

        {showingDeleteWeekAlert && (
          <CustomAlert
            isVisible={showingDeleteWeekAlert}
            onClose={() => setShowingDeleteWeekAlert(false)}
            title={"deleteWeekAlertTitle-string"}
            text={t("deleteWeekAlertText-string", { week: weekToDelete })}
            action={confirmDeleteWeek}
            specialCTA={"delete-string"}
          />
        )}

        {showingDuplicateWeek && block && weekToDuplicate && (
          <DuplicateWeekModal
            isVisible={showingDuplicateWeek}
            onClose={() => {
              setShowingDuplicateWeek(false);
              setWeekToDuplicate(null);
            }}
            block={block}
            sourceWeek={weekToDuplicate}
          />
        )}
      </View>
    );
  }
);

// Wrapper component that provides the drag context
const BlockView = observer(
  ({
    block,
    showTutorialLink = false,
    showTutorialBanner = false,
    showExerciseHistory = true,
    onSendFeedback,
    onCloseTutorialLink,
    onCloseTutorialBanner,
    onAccessOld,
    tutorialUrlPath,
  }) => {
    return (
      <ExerciseDragProvider>
        <BlockViewInner
          block={block}
          showTutorialLink={showTutorialLink}
          showTutorialBanner={showTutorialBanner}
          showExerciseHistory={showExerciseHistory}
          onSendFeedback={onSendFeedback}
          onCloseTutorialLink={onCloseTutorialLink}
          onCloseTutorialBanner={onCloseTutorialBanner}
          onAccessOld={onAccessOld}
          tutorialUrlPath={tutorialUrlPath}
        />
      </ExerciseDragProvider>
    );
  }
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  topBannerContainer: {
    position: "relative",
    zIndex: 101,
  },
  // Sticky header that sticks to top of page scroll
  stickyHeaderContainer: {
    position: "sticky",
    top: 0,
    zIndex: 100,
    backgroundColor: Colors.backgroundColor,
  },
  // Protocol info section inside sticky header
  protocolsGuideContainer: {
    marginBottom: 12,
  },
  // Clip container to hide the overflow from translated headers
  stickyHeaderClip: {
    width: "100%",
    overflow: "hidden",
    position: "relative",
  },
  // Container for week headers that gets translated
  headerColumnsContainer: {
    flexDirection: "row",
    gap: BlockPlanificationConstants.COLUMN_GAP,
    // Performance optimizations for smooth scrolling
    willChange: "transform", // Hint browser to optimize for transforms
    // Prevent flickering during transforms
    backfaceVisibility: "hidden",
    WebkitBackfaceVisibility: "hidden",
    // Smooth micro-transition for visual polish (very short to avoid lag perception)
    // transitionProperty: "transform",
    // transitionDuration: "16ms",
    // transitionTimingFunction: "linear",
  },
  headerScrollOverlay: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 2,
  },
  edgeFade: {
    position: "absolute",
    top: 0,
    bottom: 0,
    width: 16,
  },
  edgeFadeWide: {
    width: 36,
  },
  edgeFadeLeft: {
    left: 0,
  },
  edgeFadeRight: {
    right: 0,
  },
  edgeBlocker: {
    position: "absolute",
    top: 0,
    bottom: 0,
    width: 16,
  },
  edgeBlockerLeft: {
    left: 0,
  },
  edgeBlockerRight: {
    right: 0,
  },
  scrollButton: {
    position: "absolute",
    top: 0,
    bottom: 0,
    width: 32,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 6,
    backgroundColor: "transparent",
    opacity: 0.4,
  },
  scrollButtonLeft: {
    left: 0,
    alignItems: "flex-start",
  },
  scrollButtonRight: {
    right: 0,
    alignItems: "flex-end",
  },
  // Virtualized list container
  virtualizedContainer: {
    flex: 1,
  },
  weekColumn: {
    width: BlockPlanificationConstants.COLUMN_WIDTH,
    paddingTop: 16,
    // paddingBottom: 40,
  },
  workoutsList: {
    gap: BlockPlanificationConstants.WORKOUT_GAP,
    marginBottom: BlockPlanificationConstants.WORKOUT_GAP,
  },
  addWorkoutButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 2,
    marginTop: -2,
    // paddingVertical: 8,
    // paddingHorizontal: 12,
    // borderRadius: 12,
    // borderWidth: 1,
    // borderColor: Colors.borderColor,
    // borderStyle: "dashed",
    // gap: 6,
  },
  addWorkoutText: {
    fontSize: 12,
    fontWeight: "500",
    color: Colors.grayColor,
  },
});

export default BlockView;
